import React, { useState } from "react";
import { SR_VOICE_ENABLED } from "../sr-constants";
import { SrChatComposerActions } from "./sr-chat-composer-actions";
import { useSrVoiceRecorder } from "./useSrVoiceRecorder";

type Props = {
    disabled?: boolean;
    onSend: (text: string) => void;
    onSendVoice?: (blob: Blob, mime: string) => void;
};

export const SrAgentChatInput: React.FC<Props> = ({
    disabled,
    onSend,
    onSendVoice,
}) => {
    const [value, setValue] = useState("");
    const [focused, setFocused] = useState(false);
    const { isRecording, error, start, stop, cancel } = useSrVoiceRecorder();
    const voiceOn = SR_VOICE_ENABLED && !!onSendVoice;
    const canSend = !!value.trim() && !disabled && !isRecording;

    const submit = () => {
        if (!canSend) return;
        onSend(value.trim());
        setValue("");
    };

    const toggleVoice = async () => {
        if (disabled || !voiceOn) return;
        if (isRecording) {
            const result = await stop();
            if (result) onSendVoice?.(result.blob, result.mime);
            return;
        }
        if (value.trim()) setValue("");
        await start();
    };

    const shellCls = focused
        ? "border-[#3A80F4]/55 shadow-[0_0_0_3px_rgba(58,128,244,0.12)]"
        : "border-slate-200 shadow-sm";

    return (
        <div className="space-y-2">
            {isRecording && (
                <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />
                    Listening… tap stop to send
                    <button
                        type="button"
                        className="ml-auto text-slate-500 underline-offset-2 hover:underline"
                        onClick={cancel}
                    >
                        Cancel
                    </button>
                </div>
            )}
            {error && <p className="px-1 text-xs text-red-600">{error}</p>}

            <div
                className={`rounded-2xl border bg-white transition-shadow ${shellCls} ${
                    isRecording ? "border-red-200" : ""
                }`}
            >
                <textarea
                    rows={3}
                    value={value}
                    disabled={disabled || isRecording}
                    placeholder={
                        isRecording
                            ? "Recording your question…"
                            : "Type a question about service requests…"
                    }
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    className="sr-chat-input w-full resize-none bg-transparent px-4 pb-2 pt-3.5 text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <SrChatComposerActions
                    canSend={canSend}
                    disabled={disabled}
                    isRecording={isRecording}
                    voiceOn={voiceOn}
                    onSend={submit}
                    onToggleVoice={toggleVoice}
                />
            </div>
        </div>
    );
};
